/**
 * equipApiExtras.ts
 *
 * EQuIP-specific API calls that are NOT synced from Keystone Platform.
 * update.sh only syncs equipApi.ts and askQuestionService.ts from services/ —
 * this file is safe from overwrites.
 *
 * MAINT-01: getRosterProfile lives here to avoid regression via update.sh.
 */

import type { RosterProfile } from '@/lib/equip-extras'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
const EQUIP_ENDPOINT = `${API_BASE_URL}/api/equip`

/**
 * fetchEquip — internal helper, mirrors the fetchWithCredentials pattern in equipApi.ts
 * (fetchWithCredentials is not exported from equipApi.ts so we define our own here).
 * 90-second timeout matches the equipApi.ts pattern for long-running endpoints.
 */
async function fetchEquip<T>(url: string): Promise<T> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 90000)
  try {
    const response = await fetch(url, {
      credentials: 'include',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `HTTP ${response.status}`)
    }
    return response.json() as Promise<T>
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * getRosterProfile
 *
 * Fetches the authenticated user's roster profile from GET /api/equip/me/roster-profile.
 * Returns roleId (one of the 5 framework role IDs, or null if not mappable),
 * employmentStatus, isPhysician, and discipline.
 */
export const getRosterProfile = (): Promise<RosterProfile> =>
  fetchEquip<RosterProfile>(`${EQUIP_ENDPOINT}/me/roster-profile`)
